import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { defaultHomePathForUser } from '@/lib/plan-access';
import { settingsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { ArrowRight, CheckCircle2, Loader2 } from 'lucide-react';

const CALENDLY_URL = 'https://calendly.com/hello-protegoos/30min';
const CALENDLY_SCRIPT = 'https://assets.calendly.com/assets/external/widget.js';

/**
 * Inline Calendly booking (Account Connection Session).
 *
 * After booking we navigate INSIDE Protego (POA intake / dashboard).
 * Do NOT rely on Calendly's "Confirmation redirect URL" — that opens
 * calendly.com's "You are leaving Calendly" interstitial inside the iframe.
 * In Calendly event settings: leave Confirmation redirect empty (or remove app.section3.ai).
 */
export default function CalendlyPage() {
  const hostRef = useRef(null);
  const navigatedRef = useRef(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [booked, setBooked] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  const fromCheckout =
    searchParams.get('from') === 'checkout' ||
    searchParams.get('from') === 'poa_paid' ||
    searchParams.get('from') === 'payment';

  const continuePath = useMemo(() => defaultHomePathForUser(user), [user]);

  const goToWorkspace = (replace = true) => {
    if (navigatedRef.current) return;
    navigatedRef.current = true;
    setRedirecting(true);
    navigate(continuePath, { replace });
  };

  useEffect(() => {
    const ensureScript = () => {
      if (window.Calendly) {
        return Promise.resolve();
      }
      const existing = document.querySelector(`script[src="${CALENDLY_SCRIPT}"]`);
      if (existing) {
        return new Promise((resolve) => {
          existing.addEventListener('load', () => resolve(), { once: true });
        });
      }
      return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = CALENDLY_SCRIPT;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = reject;
        document.body.appendChild(script);
      });
    };

    let cancelled = false;
    let redirectTimer;

    ensureScript()
      .then(() => {
        if (cancelled || !hostRef.current || !window.Calendly) return;
        hostRef.current.innerHTML = '';
        window.Calendly.initInlineWidget({
          url: CALENDLY_URL,
          parentElement: hostRef.current,
        });
      })
      .catch(() => {
        // Fallback: leave the data-url div for Calendly's auto-init script.
      });

    const onMessage = (event) => {
      if (event.origin !== 'https://calendly.com') return;
      // Payload can be a string in older embeds — normalize.
      const data = typeof event.data === 'string'
        ? (() => { try { return JSON.parse(event.data); } catch { return null; } })()
        : event.data;
      if (!data || data.event !== 'calendly.event_scheduled') return;
      // Real booking only — invitee URI present.
      if (!data.payload?.invitee?.uri && !data.payload?.event?.uri) return;

      setBooked(true);
      try {
        sessionStorage.setItem('protego_connection_session_booked', '1');
      } catch {
        // ignore
      }
      settingsAPI.bookingConfirmed({
        call_join_link: CALENDLY_URL,
        reschedule_link: CALENDLY_URL,
        booking_url: CALENDLY_URL,
      }).catch(() => {});

      // Leave this page inside our SPA so Calendly never shows
      // "You are leaving Calendly → app.section3.ai".
      redirectTimer = window.setTimeout(() => {
        if (!cancelled) goToWorkspace(true);
      }, 900);
    };
    window.addEventListener('message', onMessage);

    return () => {
      cancelled = true;
      window.clearTimeout(redirectTimer);
      window.removeEventListener('message', onMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- navigate once on schedule
  }, [continuePath]);

  return (
    <div className="space-y-4" data-testid="calendly-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold">Account Connection Session</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Book a call below to connect your account
          </p>
          {fromCheckout && !booked && (
            <p className="text-xs text-primary mt-2">
              Payment confirmed. Book your connection session — we will open your workspace right after.
            </p>
          )}
          {booked && (
            <p className="mt-2 flex items-center gap-1.5 text-sm text-emerald-400">
              {redirecting ? (
                <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
              ) : (
                <CheckCircle2 className="h-4 w-4 shrink-0" />
              )}
              {redirecting
                ? 'Session booked — opening your workspace…'
                : 'Session booked — continuing to your workspace…'}
            </p>
          )}
        </div>
        {(fromCheckout || booked) && (
          <Button type="button" onClick={() => goToWorkspace(false)} className="shrink-0" disabled={redirecting}>
            {booked ? 'Continue now' : 'Skip for now — go to app'}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        )}
      </div>
      <div
        ref={hostRef}
        className="calendly-inline-widget w-full overflow-hidden rounded-lg border border-border/60 bg-card"
        data-url={CALENDLY_URL}
        style={{ minWidth: 320, height: 700 }}
      />
    </div>
  );
}
